'use client';

import { useDataTableSearch } from '../makerkit/use-data-table-search';

export interface UseDebouncedSearchParamOptions {
  /** Debounce delay in milliseconds. @default 300 */
  debounceMs?: number;
  /**
   * URL params to reset on a new term. `'page'` → `'1'`; any other key is
   * deleted (e.g. `'cursor'` for keyset-paginated surfaces).
   * @default ['page']
   */
  resetParams?: string[];
}

export interface UseDebouncedSearchParamResult {
  /** Local (immediate) input value. */
  value: string;
  /** Committed URL value (after debounce). */
  committed: string;
  /** Update the term (debounced URL write). */
  setValue: (value: string) => void;
  /** Clear immediately. */
  clear: () => void;
  /** Whether a debounce is in flight. */
  isPending: boolean;
}

/**
 * URL-synced, debounced single search param — a key-first façade over
 * {@link useDataTableSearch} so non-DataTable surfaces (e.g. the marketplace
 * filter-the-grid box) share one implementation instead of re-rolling
 * useState + setTimeout + router.push.
 *
 * @example
 * // marketplace: reset the keyset cursor (not page) when the query changes
 * const search = useDebouncedSearchParam('q', { resetParams: ['cursor'] });
 */
export function useDebouncedSearchParam(
  key: string,
  options: UseDebouncedSearchParamOptions = {},
): UseDebouncedSearchParamResult {
  const { query, urlQuery, setQuery, clearQuery, isSearching } =
    useDataTableSearch({
      paramName: key,
      debounceMs: options.debounceMs,
      resetParams: options.resetParams,
    });

  return {
    value: query,
    committed: urlQuery,
    setValue: setQuery,
    clear: clearQuery,
    isPending: isSearching,
  };
}
